<template>
  <div v-if="loading" class="loading-screen">
    <img style="width:20px; height:20px" src="@/assets/hamster.gif" alt="Loading..." />
    <p>Loading, please wait...</p>
  </div>
  <div v-else class="list row">
    <div class="col-md-8">
      <div class="input-group mb-3">
        <input
          type="text"
          class="form-control"
          placeholder="Search by name, email, or client ID"
          v-model="searchName"
          @keyup.enter="searchNameMethod"
        />
        <div class="input-group-append">
          <button class="btn btn-outline-secondary" type="button" @click="searchNameMethod">
            Search
          </button>
        </div>
        <button class="btn btn-primary ms-3" data-bs-toggle="modal" data-bs-target="#createClientModal">
          Create Client
        </button>
      </div>
    </div>
    <div class="col-md-12">
      <h4>Clients List</h4>
      <table class="table">
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">First Name</th>
            <th scope="col">Last Name</th>
            <th scope="col">Email</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(client) in clients" :key="client.clientid">
            <th scope="row">{{ client.clientid }}</th>
            <td>{{ client.person ? client.person.firstname : 'N/A' }}</td>
            <td>{{ client.person ? client.person.lastname : 'N/A' }}</td>
            <td>{{ client.person ? client.person.email : 'N/A' }}</td>
            <td>
              <router-link :to="{ name: 'client', params: { id: client.clientid }}">Edit</router-link>
            </td>
          </tr>
        </tbody>
      </table>
      <nav aria-label="Page navigation">
        <ul class="pagination">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="changePage(currentPage - 1)" aria-label="Previous">
              <span aria-hidden="true">&laquo;</span>
            </button>
          </li>
          <li v-if="totalPages > 1" class="page-item" :class="{ active: currentPage === 1 }">
            <button class="page-link" @click="changePage(1)">1</button>
          </li>
          <li v-if="currentPage > 4">
            <span class="page-link">...</span>
          </li>
          <li
            v-for="page in pagesToShow"
            :key="page"
            class="page-item"
            :class="{ active: currentPage === page }"
          >
            <button class="page-link" @click="changePage(page)">{{ page }}</button>
          </li>
          <li v-if="currentPage < totalPages - 3">
            <span class="page-link">...</span>
          </li>
          <li v-if="totalPages > 1" class="page-item" :class="{ active: currentPage === totalPages }">
            <button class="page-link" @click="changePage(totalPages)">{{ totalPages }}</button>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <button class="page-link" @click="changePage(currentPage + 1)" aria-label="Next">
              <span aria-hidden="true">&raquo;</span>
            </button>
          </li>
        </ul>
      </nav>
    </div>

    <!-- Modal -->
    <div ref="createClientModal" class="modal fade" id="createClientModal" tabindex="-1" aria-labelledby="createClientModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="createClientModalLabel">Create New Client</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveNewClient">
              <div class="mb-3">
                <label for="firstName" class="form-label">First Name</label>
                <input type="text" class="form-control" id="firstName" v-model="newClient.firstName" required />
              </div>

              <div class="mb-3">
                <label for="lastName" class="form-label">Last Name</label>
                <input type="text" class="form-control" id="lastName" v-model="newClient.lastName" required />
              </div>

              <div class="mb-3">
                <label for="email" class="form-label">Email</label>
                <input type="email" class="form-control" id="email" v-model="newClient.email" required />
              </div>

              <div class="mb-3">
                <label for="mobilePhone" class="form-label">Mobile Phone</label>
                <input type="tel" class="form-control" id="mobilePhone" v-model="newClient.mobilePhone" />
              </div>

              <div class="mb-3">
                <label for="country" class="form-label">Country</label>
                <input type="text" class="form-control" id="country" v-model="newClient.country" />
              </div>

              <button type="submit" class="btn btn-success">Save Client</button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import ClientsDataService from "../../services/ClientsDataService.js";

export default {
  name: "passengers-list",
  data() {
    return {
      clients: [],
      searchName: "",
      currentPage: 1,
      pageSize: 50,
      totalItems: 0,
      newClient: {
        firstName: "",
        lastName: "",
        email: "",
        mobilePhone: "",
        country: "",
      },
      loading: true,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.pageSize);
    },
    pagesToShow() {
      const range = [];
      const start = Math.max(2, this.currentPage - 2);
      const end = Math.min(this.totalPages - 1, this.currentPage + 2);
      for (let i = start; i <= end; i++) {
        range.push(i);
      }
      return range;
    },
  },
  methods: {
    async saveNewClient() {
      this.isLoading = true;

      try {
        // Check if the client already exists
        const response = await ClientsDataService.findByCriteria({
          firstName: this.newClient.firstName,
          lastName: this.newClient.lastName,
          email: this.newClient.email,
        });

        if (response.data.exists) {
          alert("A client with the same first name, last name, and email already exists.");
          this.isLoading = false;
          return;
        }

        // Create the client if no match is found
        await ClientsDataService.create(this.newClient);

        alert("Client created successfully!");
        this.closeClientModal();
        this.retrieveClients(); // Refresh the client list
      } catch (error) {
        console.error("Error creating client:", error);
        alert("An error occurred while creating the client.");
      } finally {
        this.isLoading = false;
      }
    },
    closeClientModal() {
      const modal = this.$refs.createClientModal;
      modal.style.display = "none";
      document.body.classList.remove("modal-open");

      const backdrop = document.querySelector(".modal-backdrop");
      if (backdrop) {
        backdrop.parentNode.removeChild(backdrop);
      }

      this.newClient = {
        firstName: "",
        lastName: "",
        email: "",
        mobilePhone: "",
        country: "",
      };
    },
    retrieveClients(page = 1) {
      this.loading = true;
      ClientsDataService.getAll({ page, pageSize: this.pageSize })
        .then((response) => {
          this.clients = response.data.clients;
          this.totalItems = response.data.totalItems;
          this.currentPage = page;
        })
        .catch((e) => {
          console.log(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    searchNameMethod() {
      ClientsDataService.findByName(this.searchName)
        .then((response) => {
          this.clients = response.data.clients || [];
          this.totalItems = response.data.totalItems || 0;
          this.currentPage = 1;
        })
        .catch((e) => {
          console.log(e);
        });
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.retrieveClients(page);
      }
    },
  },
  mounted() {
    this.retrieveClients();
  },
};
</script>

<style>
.list {
  text-align: left;
  max-width: 950px;
  margin: auto;
}

.loading-screen {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  text-align: center;
}

.loading-screen img {
  width: 100px;
  height: 100px;
  margin-bottom: 20px;
}
</style>
